/**
 * SchoolViewAs - Admin "View As" role masquerading
 * Generates temporary tokens for admin to preview Teacher/Student/Parent portals
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Eye, UserCog, GraduationCap, Users2, ExternalLink, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ROLES = [
  { key: 'teacher', label: 'Teacher', icon: UserCog, color: 'bg-blue-500', desc: 'View the teacher dashboard, classes, gradebook, and messaging' },
  { key: 'student', label: 'Student', icon: GraduationCap, color: 'bg-emerald-500', desc: 'View the student portal — assignments, grades, timetable' },
  { key: 'parent', label: 'Parent', icon: Users2, color: 'bg-amber-500', desc: 'View the parent portal — child progress, attendance, messaging' },
];

export const SchoolViewAs = ({ tenantId }) => {
  const navigate = useNavigate();
  const [tokens, setTokens] = useState(null);

  const viewAsMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/school/tenants/${tenantId}/view-as`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to generate tokens');
      return res.json();
    },
    onSuccess: (data) => {
      setTokens(data);
      toast.success('Preview tokens generated');
    },
    onError: (e) => toast.error(e.message),
  });

  const openPortal = (role, token) => {
    // Store the token and open in new tab
    const portalPaths = {
      teacher: `/school/${tenantId}/teacher`,
      student: `/school/${tenantId}/student`,
      parent: `/school/${tenantId}/parent`,
    };
    // Open in new window with token injected
    const url = `${window.location.origin}${portalPaths[role]}?preview_token=${token}`;
    window.open(url, '_blank');
  };

  return (
    <div className="space-y-6" data-testid="school-view-as-panel">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-indigo-500" />
            View As — Role Preview
          </CardTitle>
          <CardDescription>
            Preview the school portals as a Teacher, Student, or Parent. This generates temporary read-only tokens
            so you can see exactly what each user sees.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!tokens ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center mx-auto mb-4">
                <Eye className="h-8 w-8 text-indigo-500" />
              </div>
              <p className="text-slate-500 mb-4">Generate temporary preview tokens to view each portal</p>
              <Button onClick={() => viewAsMutation.mutate()} disabled={viewAsMutation.isPending}
                      data-testid="generate-view-as-btn">
                {viewAsMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Generate Preview Tokens
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>Preview tokens are temporary and expire after the standard session duration.</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {ROLES.map(role => {
                  const data = tokens[role.key];
                  const available = !!data;
                  return (
                    <Card key={role.key} className={!available ? 'opacity-50' : ''}>
                      <CardContent className="pt-6 text-center space-y-3">
                        <div className={`w-12 h-12 rounded-full ${role.color} flex items-center justify-center mx-auto`}>
                          <role.icon className="h-6 w-6 text-white" />
                        </div>
                        <h3 className="font-semibold text-slate-900">{role.label}</h3>
                        {available ? (
                          <>
                            <p className="text-xs text-slate-500">{data.name}</p>
                            <p className="text-xs text-slate-400">{data.email}</p>
                            <Button size="sm" className="w-full" onClick={() => openPortal(role.key, data.token)}
                                    data-testid={`view-as-${role.key}-btn`}>
                              <ExternalLink className="h-3 w-3 mr-1" /> Open {role.label} Portal
                            </Button>
                          </>
                        ) : (
                          <div>
                            <Badge variant="secondary">No {role.label} found</Badge>
                            <p className="text-xs text-slate-400 mt-2">{role.desc}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <div className="flex justify-center pt-2">
                <Button variant="outline" size="sm" onClick={() => viewAsMutation.mutate()}
                        disabled={viewAsMutation.isPending}>
                  {viewAsMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Regenerate Tokens
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SchoolViewAs;
